/**
 * The version selector (T2.12).
 *
 * design.md § 2 sets the vocabulary and asks that it not be tidied: the version
 * statuses read as **Draft** and **Live** in the interface, because "Published"
 * is also a deliverable state and showing both words in one screen confuses
 * users. They are different types in code — `VersionStatus` here, never
 * `DeliverableState` — and neither is touched by terminology overrides.
 *
 * A viewer never sees a draft (design.md D-2). The API already omits them, and
 * this component filters them again. That is not redundancy for its own sake:
 * the rule is that a viewer must not reach a draft by ANY route, and a single
 * enforcement point that happens to be on the other side of the network is one
 * bug away from failing open.
 */
import type { BlueprintRole, VersionSummary } from '../../lib/blueprints';

function versionLabel(version: VersionSummary): string {
  if (version.status === 'draft') return `Version ${version.versionNo} — Draft`;

  const when = version.publishedAt ? new Date(version.publishedAt) : null;
  const date =
    when && !Number.isNaN(when.getTime())
      ? when.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
      : null;

  const suffix = version.isLatest ? 'Live' : 'Superseded';
  return `Version ${version.versionNo} — ${suffix}${date ? ` · ${date}` : ''}`;
}

export function VersionSelector({
  versions,
  role,
  selectedId,
  onSelect,
}: {
  versions: VersionSummary[];
  role: BlueprintRole;
  selectedId: string | null;
  onSelect: (versionId: string) => void;
}) {
  const visible = role === 'viewer' ? versions.filter((v) => v.status !== 'draft') : versions;

  // Nothing to choose between is not a control — it is a label, and showing a
  // one-entry dropdown just invites a click that does nothing.
  if (visible.length <= 1) return null;

  return (
    <label className="version-select">
      <span className="field-label">Version</span>
      <select
        value={selectedId ?? ''}
        onChange={(e) => onSelect(e.target.value)}
      >
        {visible.map((v) => (
          <option value={v.id} key={v.id}>
            {versionLabel(v)}
          </option>
        ))}
      </select>
    </label>
  );
}

export { versionLabel };
